import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/enhanced-button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { login, validateSession } from "@/services/auth.service";
import { loginSchema, type LoginFormValues } from "@/schemas/auth.schema";
import { startInitialSync, startFullSync } from "@/services/sync.quickbooks.service";
import posthog, { registerSession, captureError } from "@/lib/posthog";

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const navigateAfterAuth = useCallback(async (sessionData: { hasIntegration?: boolean; hasCompletedSync?: boolean; integrationStatus?: string; organization?: { id: string; name: string } | null; loginSyncQueued?: boolean }) => {
    const loginSyncQueued = sessionData.loginSyncQueued === true;

    if (sessionData.integrationStatus === 'error') {
      // Expired existing connection → go to dashboard (will show reconnect warning)
      // First-time connection error → go to connect screen
      if (sessionData.hasIntegration) {
        navigate("/dashboard", { replace: true, state: { loginSyncQueued } });
      } else {
        navigate("/onboarding/connect", { replace: true });
      }
      return;
    }
    if (sessionData.hasIntegration && sessionData.hasCompletedSync) {
      // A returning user already has completed-sync data in our DB, so there is
      // nothing to wait for — refreshing it is a background concern. Go straight
      // to the dashboard, which shows the sync as a non-blocking progress
      // indicator. The full-screen /onboarding/syncing gate is only for cases
      // where there genuinely is no data to show yet (see below).
      //
      // The login endpoint queues the sync itself (loginSyncQueued). A restored
      // session skips that endpoint, so queue it here instead — awaiting the
      // request only waits for the SyncStatus row to be written, which the
      // dashboard poller needs in order to attach to this sync rather than the
      // previous completed one.
      if (!loginSyncQueued) {
        await startFullSync().catch(() => {
          // Sync failed to queue — the dashboard still renders the data we have
        });
      }
      navigate("/dashboard", { replace: true, state: { loginSyncQueued: true } });
      return;
    }
    if (sessionData.hasIntegration) {
      // Integration exists but no sync has ever completed (e.g. a first sync
      // was interrupted before finishing). The dashboard would be empty, so
      // block behind the full-screen sync gate exactly like a first-time
      // QuickBooks connection, rather than trusting hasIntegration alone.
      try {
        const syncStatusId = await startInitialSync();
        navigate("/onboarding/syncing", { replace: true, state: { syncStatusId } });
      } catch {
        // Sync failed to start — go to dashboard, data may be stale/empty
        navigate("/dashboard", { replace: true, state: { loginSyncQueued } });
      }
      return;
    }
    // No integration — redirect to the right onboarding step
    if (sessionData.organization) {
      navigate("/onboarding/connect", { replace: true });
    } else {
      navigate("/onboarding/organization", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const sessionData = await validateSession();
        if (sessionData) {
          await navigateAfterAuth(sessionData);
          return;
        }
      } catch {
        // No valid session, show login page
      }
      setCheckingSession(false);
    };

    checkSession();
  }, [navigate, navigateAfterAuth]);

  const onSubmit = async (values: LoginFormValues) => {
    setIsLoading(true);

    try {
      const sessionData = await login(values.email, values.password);
      if (sessionData.user) {
        posthog.identify(sessionData.user.id, {
          email: sessionData.user.email,
          firstName: sessionData.user.firstName,
          lastName: sessionData.user.lastName,
        });
        registerSession();
      }
      posthog.capture("user_logged_in", {
        email: values.email,
        has_integration: sessionData.hasIntegration ?? false,
        has_organization: !!sessionData.organization,
      });
      await navigateAfterAuth(sessionData);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed";
      const isUnverified = message.toLowerCase().includes("verif") || message.toLowerCase().includes("confirm");
      if (isUnverified) {
        navigate("/verify-email-pending", { state: { email: values.email } });
        return;
      }
      posthog.capture("login_failed", { error_type: message });
      captureError(error, { context: 'login' });
      toast({
        title: "Login Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur-sm">
          <CardContent className="p-8">
            <div className="mb-8 text-center">
              <div className="w-32 h-32 mx-auto mb-4">
                <img
                  src="/content/logo.png"
                  alt="BlinkBooks Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Welcome back
              </h1>
              <p className="text-muted-foreground">
                Sign in to your BlinkBooks account
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="you@company.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center mt-1"
                >
                  Forgot password?
                </button>
              </form>
            </Form>

            <p className="mt-6 text-center text-sm text-muted-foreground">
              Don't have an account?{" "}
              <button
                onClick={() => navigate("/register")}
                className="underline hover:text-foreground transition-colors font-medium"
              >
                Sign up
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
